import React, { useState, useEffect } from 'react';
import { X, Save, Loader2, Building2 } from 'lucide-react';
import CustomSelect from '../shared/CustomSelect';

export const CompanyFormModal = ({
                                     isOpen,
                                     onClose,
                                     onSubmit,
                                     company = null,
                                     loading = false
                                 }) => {
    const [formData, setFormData] = useState({
        code: '',
        name: '',
        description: '',
        businessScope: 'INSURANCE',
        active: true
    });

    const [errors, setErrors] = useState({});

    const businessScopes = [
        { value: 'INSURANCE', label: 'Insurance' },
        { value: 'INVESTMENTS', label: 'Investments' },
        { value: 'BANKING', label: 'Banking' },
        { value: 'IT', label: 'IT Services' },
        { value: 'HEALTHCARE', label: 'Healthcare' },
        { value: 'RETAIL', label: 'Retail' }
    ];

    useEffect(() => {
        if (company) {
            setFormData({
                code: company.code || '',
                name: company.name || '',
                description: company.description || '',
                businessScope: company.businessScope || 'INSURANCE',
                active: company.active !== undefined ? company.active : true
            });
        } else {
            setFormData({
                code: '',
                name: '',
                description: '',
                businessScope: 'INSURANCE',
                active: true
            });
        }
        setErrors({});
    }, [company, isOpen]);

    const validateForm = () => {
        const newErrors = {};

        if (!formData.code.trim()) newErrors.code = 'Company code is required';
        if (!formData.name.trim()) newErrors.name = 'Company name is required';
        if (formData.code.length > 10) newErrors.code = 'Code must be 10 characters or less';

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (validateForm()) {
            onSubmit(formData);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-screen items-center justify-center p-4">
                <div
                    className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
                    onClick={onClose}
                />

                <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                    <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                                    <Building2 className="w-6 h-6 text-blue-600" />
                                </div>
                                <h3 className="text-xl font-semibold text-gray-900">
                                    {company ? 'Edit Company' : 'Add New Company'}
                                </h3>
                            </div>
                            <button
                                onClick={onClose}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                    </div>

                    <form onSubmit={handleSubmit} className="p-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                            {/* Company Code */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Company Code *
                                </label>
                                <input
                                    type="text"
                                    value={formData.code}
                                    onChange={(e) => setFormData({...formData, code: e.target.value.toUpperCase()})}
                                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                        errors.code ? 'border-red-500' : 'border-gray-300'
                                    }`}
                                    placeholder="e.g., INS001"
                                    disabled={company !== null} // Disable code editing when updating
                                />
                                {errors.code && (
                                    <p className="mt-1 text-sm text-red-600">{errors.code}</p>
                                )}
                            </div>

                            {/* Company Name */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Company Name *
                                </label>
                                <input
                                    type="text"
                                    value={formData.name}
                                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                        errors.name ? 'border-red-500' : 'border-gray-300'
                                    }`}
                                    placeholder="Enter company name"
                                />
                                {errors.name && (
                                    <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                                )}
                            </div>

                            {/* Business Scope */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Business Scope
                                </label>
                                <CustomSelect
                                    value={formData.businessScope}
                                    onChange={(val) => setFormData({...formData, businessScope: val})}
                                    options={businessScopes}
                                    placeholder="Select business scope"
                                />
                            </div>

                            {/* Status */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Status
                                </label>
                                <div className="flex items-center gap-4 mt-3">
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={formData.active}
                                            onChange={(e) => setFormData({...formData, active: e.target.checked})}
                                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                        />
                                        <span className="select-none">Active Company</span>
                                    </label>
                                </div>
                            </div>

                            {/* Description */}
                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Description
                                </label>
                                <textarea
                                    value={formData.description}
                                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    rows="4"
                                    placeholder="Enter company description..."
                                />
                            </div>
                        </div>

                        <div className="flex gap-3 justify-end pt-4 border-t border-gray-200">
                            <button
                                type="button"
                                onClick={onClose}
                                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={loading}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50 flex items-center gap-2"
                            >
                                {loading ? (
                                    <>
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        Saving...
                                    </>
                                ) : (
                                    <>
                                        <Save className="w-4 h-4" />
                                        {company ? 'Update' : 'Create'} Company
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default CompanyFormModal;
